import ChevronDownIcon from "@shared/icons/ChevronDownIcon";
import CsvIcon from "@shared/icons/CsvIcon";
import DownloadIcon from "@shared/icons/DownloadIcon";
import ExcelIcon from "@shared/icons/ExcelIcon";
import PdfIcon from "@shared/icons/PdfIcon";
import {
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemText,
  Popover,
} from "@material-ui/core";
import React, { useId, useRef, useState } from "react";

/**
 * The export control: a trigger that opens a MENU of output formats.
 *
 * IT ADVERTISES A MENU, SO IT IS ONE. The trigger has carried
 * `aria-haspopup="menu"` since the affordance was completed, and for a while
 * the thing it opened was a plain list of buttons — no `menu` role, no
 * `menuitem` roles and none of the keyboard behaviour a reader who is told
 * "menu" then goes looking for. A promise in an attribute that the widget does
 * not keep is worse than no promise: a screen-reader user is told to press
 * Down and nothing moves.
 *
 * What that contract is, in full:
 *
 *   - the list is `role="menu"`, named by the trigger that opens it;
 *   - each row is `role="menuitem"`, and the `li` around it is `role="none"`
 *     so it does not present as a list item inside a menu;
 *   - opening moves focus to the first row, so the menu is operable from the
 *     keyboard the moment it appears;
 *   - Down and Up step through the rows and wrap; Home and End jump to the
 *     ends;
 *   - Escape closes the menu, and focus returns to the trigger;
 *   - choosing a format closes the menu, and focus returns to the trigger.
 *
 * The two focus returns are one line — the popover tells us when it has gone —
 * and they are what stops a keyboard reader being dropped at the top of the
 * document every time they export a report.
 */
const ExportButton = ({
  exportLoading,
  onExport,
  onDownloadPDF,
  pdfLoading,
  exportsDisabled,
  showCSV = true,
  showExcel = true,
  showPDF = true,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const menuRef = useRef(null);
  const triggerRef = useRef(null);
  const triggerId = useId();

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const open = Boolean(anchorEl);
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleExport = (format) => {
    onExport(format);
    handleClose();
  };

  /**
   * The PDF row goes through its own callback and NOT through `handleExport`,
   * because one consumer wires it to a different endpoint. It still has to
   * close the menu: choosing a format is choosing a format, and a row that
   * dispatched an export and left the menu standing over it was the one row of
   * the three that did. It also never handed focus back, because the return is
   * hung on the popover leaving and the popover never left.
   */
  const handleDownloadPDF = () => {
    onDownloadPDF();
    handleClose();
  };

  /**
   * The rows a reader can currently move to, read off the DOM.
   *
   * Not an array of refs: all three rows are conditional, callers turn them on
   * and off independently, and a row mid-export is disabled — so an index kept
   * in state would have to be rebuilt on every one of those changes. The DOM
   * already holds exactly the order being traversed, and `:not([disabled])`
   * keeps the rotation to the rows a pointer could use too.
   */
  const itemsOf = () => {
    const rows = menuRef.current?.querySelectorAll(
      '[role="menuitem"]:not([disabled])'
    );
    return rows ? Array.from(rows) : [];
  };

  const focusItemAt = (index) => {
    const items = itemsOf();
    if (items.length === 0) return;
    items[((index % items.length) + items.length) % items.length].focus();
  };

  /**
   * Down, Up, Home and End. Escape is deliberately absent: the popover already
   * dismisses on it and restores focus through the same path a selection takes,
   * so handling it a second time here would only be a second way to get it
   * wrong.
   */
  const handleMenuKeyDown = (event) => {
    const items = itemsOf();
    if (items.length === 0) return;
    const from = items.indexOf(window.document.activeElement);

    if (event.key === "ArrowDown") {
      event.preventDefault();
      focusItemAt(from + 1);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      focusItemAt(from === -1 ? items.length - 1 : from - 1);
    } else if (event.key === "Home") {
      event.preventDefault();
      focusItemAt(0);
    } else if (event.key === "End") {
      event.preventDefault();
      focusItemAt(items.length - 1);
    }
  };

  return (
    <div>
      <button
        aria-expanded={open}
        aria-haspopup="menu"
        className={`border-2 border-pd-blue px-16 md:px-24 py-8 md:py-12 rounded-4 flex items-center gap-8 bg-blue-gradient text-white font-bold ${
          exportsDisabled ? "opacity-50" : ""
        }`}
        disabled={exportsDisabled}
        id={triggerId}
        onClick={handleClick}
        ref={triggerRef}
        style={{
          background: "linear-gradient(270deg, #028AFD 0.05%, #015297 99.95%)",
        }}
        type="button"
      >
        {exportLoading || pdfLoading ? (
          <CircularProgress className="mx-auto" color="inherit" size={24} />
        ) : (
          <>
            <DownloadIcon className="w-24" />
            Export
            {/* The caret is the only VISIBLE half of this affordance; the
                popup attributes above are the other half, and neither is
                complete without the other. It is aria-hidden because it
                restates for sighted readers exactly what aria-haspopup
                already says to assistive technology — announcing it twice
                would be a second, wordless "menu" after the first. */}
            <ChevronDownIcon
              aria-hidden="true"
              className="w-12"
              data-testid="export-trigger-caret"
            />
          </>
        )}
      </button>
      <Popover
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        onClose={handleClose}
        open={open}
        transformOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        TransitionProps={{
          // The menu is operable from the keyboard the moment it is on screen,
          // and the trigger gets focus back the moment it leaves. Both are hung
          // on the transition rather than on the state change: the restore has
          // to happen AFTER the popover has gone, or the focus trap that is
          // still up while it fades takes the focus straight back.
          onEntered: () => focusItemAt(0),
          onExited: () => triggerRef.current?.focus(),
        }}
      >
        <List
          aria-labelledby={triggerId}
          onKeyDown={handleMenuKeyDown}
          ref={menuRef}
          role="menu"
        >
          {showPDF ? (
            <ListItem
              className="flex items-center justify-start w-full gap-16 pr-32"
              role="none"
            >
              <button
                className="flex items-center gap-8"
                disabled={pdfLoading || exportLoading}
                onClick={handleDownloadPDF}
                role="menuitem"
                type="button"
              >
                <PdfIcon className="w-24" />
                <ListItemText>PDF</ListItemText>
              </button>
            </ListItem>
          ) : null}

          {showExcel ? (
            <>
              {showPDF ? <Divider /> : null}
              <ListItem
                className="flex items-center justify-start w-full gap-16 pr-32"
                role="none"
              >
                <button
                  className="flex items-center gap-8"
                  disabled={exportLoading}
                  onClick={() => handleExport("xlsx")}
                  role="menuitem"
                  type="button"
                >
                  <ExcelIcon className="w-24" />
                  <ListItemText>Excel</ListItemText>
                </button>
              </ListItem>
            </>
          ) : null}
          {showCSV ? (
            <>
              <Divider />
              <ListItem
                className="flex items-center justify-start w-full gap-16 pr-32"
                role="none"
              >
                <button
                  className="flex items-center gap-8"
                  disabled={exportLoading}
                  onClick={() => handleExport("csv")}
                  role="menuitem"
                  type="button"
                >
                  <CsvIcon className="w-24" />
                  <ListItemText>CSV</ListItemText>
                </button>
              </ListItem>
            </>
          ) : null}
        </List>
      </Popover>
    </div>
  );
};

export default ExportButton;
